import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { adminApi } from "../../api/admin";
import { listarInstituicoes } from "../../api/instituicoes";
import { formatarTipoUsuario } from "../../api/usuarios";
import "./AdminUsuarios.scss";

export default function AdminUsuarios() {
  const [busca, setBusca] = useState("");
  const [tipo, setTipo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [instituicoes, setInstituicoes] = useState([]);

  useEffect(() => {
    listarInstituicoes()
      .then(setInstituicoes)
      .catch(() => setInstituicoes([]));
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (busca) params.busca = busca;
      if (tipo !== "") params.tipo = Number(tipo);
      const res = await adminApi.listarUsuarios(params);
      setData(res);
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao listar", "error");
    } finally {
      setLoading(false);
    }
  }, [busca, tipo, page, pageSize]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const onCriar = async () => {
    const instOpts = instituicoes
      .map((i) => `<option value="${i.id}">${i.nome}</option>`)
      .join("");
    const { value, isConfirmed } = await Swal.fire({
      title: "Criar usuário",
      html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome" />
        <input id="sw-login" class="swal2-input" placeholder="Login" />
        <input id="sw-email" class="swal2-input" placeholder="Login" type="text" />
        <input id="sw-senha" class="swal2-input" placeholder="Senha inicial (mín. 6)" type="text" />
        <select id="sw-tipo" class="swal2-input">
          <option value="1">Aluno</option>
          <option value="2">Responsável</option>
          <option value="3" selected>Coordenador</option>
        </select>
        <select id="sw-inst" class="swal2-input">${instOpts}</select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Criar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const nome = document.getElementById("sw-nome").value.trim();
        const login = document.getElementById("sw-login").value.trim();
        const email = document.getElementById("sw-email").value.trim();
        const senha = document.getElementById("sw-senha").value;
        const t = Number(document.getElementById("sw-tipo").value);
        const instituicaoId = document.getElementById("sw-inst").value;

        if (!nome || !login || !email || !senha || !instituicaoId) {
          Swal.showValidationMessage("Todos os campos são obrigatórios");
          return false;
        }
        if (login.length < 3) {
          Swal.showValidationMessage("Login precisa ter ao menos 3 caracteres");
          return false;
        }
        if (senha.length < 6) {
          Swal.showValidationMessage("Senha precisa ter ao menos 6 caracteres");
          return false;
        }
        return {
          nome,
          login,
          email,
          senhaInicial: senha,
          tipo: t,
          instituicaoId,
          exigirTrocaSenhaNoPrimeiroLogin: false,
        };
      },
    });
    if (!isConfirmed) return;
    try {
      await adminApi.criarUsuario(value);
      await Swal.fire("OK", "Usuário criado com sucesso.", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao criar", "error");
    }
  };

  const onEditar = async (u) => {
    const { value, isConfirmed } = await Swal.fire({
      title: `Editar ${u.nome}`,
      html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome" value="${u.nome}" />
        <input id="sw-email" class="swal2-input" placeholder="Login" value="${u.email}" />
        ${
          u.tipo === 4
            ? `<select id="sw-tipo" class="swal2-input" disabled><option value="4" selected>Administrador (fixo)</option></select>`
            : `<select id="sw-tipo" class="swal2-input">
                <option value="1" ${u.tipo === 1 ? "selected" : ""}>Aluno</option>
                <option value="2" ${u.tipo === 2 ? "selected" : ""}>Responsável</option>
                <option value="3" ${u.tipo === 3 ? "selected" : ""}>Coordenador</option>
              </select>`
        }
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const payload = {
          nome: document.getElementById("sw-nome").value.trim(),
          email: document.getElementById("sw-email").value.trim(),
        };
        // Admin tem tipo fixo — não envia tipo para o backend.
        if (u.tipo !== 4) {
          payload.tipo = Number(document.getElementById("sw-tipo").value);
        }
        return payload;
      },
    });
    if (!isConfirmed) return;
    try {
      await adminApi.editarUsuario(u.id, value);
      Swal.fire("OK", "Usuário atualizado", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao salvar", "error");
    }
  };

  const onExcluir = async (u) => {
    const step1 = await Swal.fire({
      title: `Excluir ${u.nome}?`,
      text: "Essa ação é irreversível. Deseja continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, continuar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!step1.isConfirmed) return;

    const step2 = await Swal.fire({
      title: "Confirmação final",
      html: `<p>O usuário <strong>${u.nome}</strong> (${u.email}) e todos os seus vínculos, eventos criados e estados serão excluídos permanentemente.</p><p><strong>Essa ação não pode ser desfeita.</strong></p>`,
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Excluir definitivamente",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!step2.isConfirmed) return;

    try {
      await adminApi.excluirUsuario(u.id);
      Swal.fire("Excluído", "Usuário removido permanentemente.", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao excluir", "error");
    }
  };

  const onResetSenha = async (u) => {
    const ok = await Swal.fire({
      title: `Resetar senha de ${u.nome}?`,
      text: "Será gerado um link único válido por 1 hora.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Gerar link",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      const res = await adminApi.resetSenha(u.id);
      const expira = new Date(res.expiraEm).toLocaleString();
      Swal.fire({
        title: "Link de redefinição gerado",
        html: `
          <p>Compartilhe este link com <b>${u.email}</b>. Expira em <b>${expira}</b>.</p>
          <textarea id="sw-link" class="swal2-textarea" readonly style="width:90%">${res.resetUrl}</textarea>
          <button type="button" id="sw-copy" class="swal2-confirm swal2-styled" style="margin-top:.5rem">Copiar link</button>
        `,
        showConfirmButton: true,
        confirmButtonText: "Fechar",
        didOpen: () => {
          document.getElementById("sw-copy").onclick = () => {
            navigator.clipboard.writeText(res.resetUrl);
            Swal.showValidationMessage("Link copiado!");
            setTimeout(() => Swal.resetValidationMessage(), 1500);
          };
        },
      });
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao gerar link", "error");
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "nome", header: "Nome" },
      { accessorKey: "email", header: "Login" },
      {
        accessorKey: "tipo",
        header: "Perfil",
        cell: (info) => formatarTipoUsuario(info.getValue()),
      },
      {
        id: "acoes",
        header: "Ações",
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="acoes">
              <button onClick={() => onEditar(u)}>Editar</button>
              <button onClick={() => onResetSenha(u)}>Reset senha</button>
              {u.tipo !== 4 && (
                <button className="danger" onClick={() => onExcluir(u)}>
                  Excluir
                </button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carregar],
  );

  const table = useReactTable({
    data: data.items || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div className="admin-usuarios">
      <header>
        <h1>Administração de usuários</h1>
        <button className="primary" onClick={onCriar}>
          Novo usuário
        </button>
      </header>

      <section className="filtros">
        <input
          type="search"
          placeholder="Buscar por nome ou login"
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={tipo}
          onChange={(e) => {
            setTipo(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos os perfis</option>
          <option value="1">Aluno</option>
          <option value="2">Responsável</option>
          <option value="3">Coordenador</option>
          <option value="4">Administrador</option>
        </select>
      </section>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <table className="tabela">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="vazio">
                  Nenhum usuário encontrado.
                </td>
              </tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="paginacao">
        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Anterior
        </button>
        <span>
          Página {page} de {totalPages} • Total: {data.total}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Próxima
        </button>
      </footer>
    </div>
  );
}
